import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Plus } from "lucide-react";
import { SkillEditorMode, type SkillFormData } from "~/components/assistant/SkillEditorForm";
import SkillEditorModal from "~/components/assistant/SkillEditorModal";
import { SkillRow } from "~/components/assistant/SkillRow";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { useDatasetAttachedSkills } from "~/pages/datasets/components/assistant/hooks/useDatasetAttachedSkills";
import SkillPickerModal from "~/pages/datasets/pages/settings/components/SkillPickerModal";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import {
  getSkill,
  invalidateAllSkillLists,
  updateSkill,
  type PartialSkill,
  type Skill,
} from "~/services/skills";
import rumClient from "~/utils/monitoring";

type DialogState = { kind: "none" } | { kind: "picker" } | { kind: "editor"; skill: Skill };

export const DatasetAISkillsTab = () => {
  const { data: dataset } = useDataset();
  const { showError, showSuccess } = useToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const { skills, isLoading, attachFromLibrary, createSkill, detach } = useDatasetAttachedSkills(
    dataset?.id
  );

  const [dialog, setDialog] = useState<DialogState>({ kind: "none" });
  const [deleteTarget, setDeleteTarget] = useState<PartialSkill | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);

  const handleEdit = async (skillId: string) => {
    try {
      const skill = await getSkill(skillId);
      setDialog({ kind: "editor", skill });
    } catch {
      showError("Unable to load skill for editing");
    }
  };

  const handleEditorSubmit = async (data: SkillFormData) => {
    if (dialog.kind !== "editor" || !dataset?.id) return;
    setEditorLoading(true);
    try {
      await updateSkill(dialog.skill.id, {
        description: data.description,
        content: data.content,
      });
      await invalidateAllSkillLists();
      showSuccess(`"${dialog.skill.name}" updated`);
      setDialog({ kind: "none" });
    } catch (e) {
      rumClient?.recordError(e);
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setEditorLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await detach(deleteTarget.id);
      showSuccess(`"${deleteTarget.name}" removed`);
      setDeleteTarget(null);
    } catch (e) {
      rumClient?.recordError(e);
      showError("Unable to remove skill from dataset");
    } finally {
      setIsDeleting(false);
    }
  };

  const editorInitialData =
    dialog.kind === "editor"
      ? {
          name: dialog.skill.name,
          description: dialog.skill.description ?? "",
          content: dialog.skill.content,
        }
      : undefined;

  return (
    <>
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <TabHeader
          title="AI Skills"
          isSystemAgent={!isOwner}
          context={isLoading ? undefined : `${skills.length} installed`}
          description="Skills define dataset-specific instructions and workflows. When this dataset is attached to a project, its skills become available to the project's AI agent."
          action={
            isOwner && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDialog({ kind: "picker" })}
                className="text-primary hover:text-primary/80"
              >
                <Plus />
                Add skill
              </Button>
            )
          }
        />

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : skills.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">No skills installed.</p>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {skills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                canEdit={isOwner}
                onEdit={() => handleEdit(skill.id)}
                onDelete={() => setDeleteTarget(skill)}
              />
            ))}
          </ul>
        )}
      </div>

      <SkillPickerModal
        open={dialog.kind === "picker"}
        onClose={() => setDialog({ kind: "none" })}
        onAttachFromLibrary={attachFromLibrary}
        onCreateSkill={createSkill}
        onCreated={() => {
          setDialog({ kind: "none" });
        }}
      />

      <SkillEditorModal
        open={dialog.kind === "editor"}
        onClose={() => setDialog({ kind: "none" })}
        onSubmit={handleEditorSubmit}
        loading={editorLoading}
        mode={SkillEditorMode.EDIT}
        initialData={editorInitialData}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && !isDeleting && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this skill?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  &ldquo;{deleteTarget.name}&rdquo; will be removed from this dataset. The skill
                  remains in your library and can be re-attached at any time.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
